import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material'
import {
  ShoppingCart,
  AccountCircle,
  Menu as MenuIcon,
  Home,
  Inventory,
  Build,
  Article,
  TrendingUp,
  Info,
  Login,
  PersonAdd,
  Receipt,
  Logout,
  Dashboard
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { useCart } from '../../contexts/CartContext'

const Navbar: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, logout } = useAuth()
  const { getCartItemCount } = useCart()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
    handleUserMenuClose()
  }

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen)
  }

  const navigationItems = [
    { label: 'Home', path: '/', icon: <Home /> },
    { label: 'Products', path: '/products', icon: <Inventory /> },
    { label: 'Services', path: '/services', icon: <Build /> },
    { label: 'Blogs', path: '/blogs', icon: <Article /> },
    { label: 'Solar Pricing', path: '/solar-pricing', icon: <TrendingUp /> },
    { label: 'About', path: '/about', icon: <Info /> },
    { label: 'Contact', path: '/contact', icon: <Info /> },
  ]

  const MobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={toggleMobileDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Box
          component="img"
          src="/logo.jpeg"
          alt="Starwave Energy"
          sx={{ height: 32, mr: 1.5, borderRadius: '50%' }}
        />
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Starwave Energy
        </Typography>
      </Box>
      <Divider />

      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={toggleMobileDrawer}
            sx={{
              color: location.pathname === item.path ? 'primary.main' : 'text.primary',
              bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>

      <Divider />

      <List>
        {currentUser ? (
          <>
            <ListItem component={Link} to="/my-orders" onClick={toggleMobileDrawer}>
              <ListItemIcon>
                <Receipt />
              </ListItemIcon>
              <ListItemText primary="My Orders" />
            </ListItem>
            {currentUser.role === 'admin' && (
              <ListItem component={Link} to="/admin" onClick={toggleMobileDrawer}>
                <ListItemIcon>
                  <Dashboard />
                </ListItemIcon>
                <ListItemText primary="Admin Dashboard" />
              </ListItem>
            )}
            <ListItem onClick={() => { handleLogout(); toggleMobileDrawer(); }}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem component={Link} to="/login" onClick={toggleMobileDrawer}>
              <ListItemIcon>
                <Login />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem component={Link} to="/register" onClick={toggleMobileDrawer}>
              <ListItemIcon>
                <PersonAdd />
              </ListItemIcon>
              <ListItemText primary="Register" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  )

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 0, sm: 2 } }}>
            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                edge="start"
                color="primary"
                aria-label="menu"
                onClick={toggleMobileDrawer}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexGrow: isMobile ? 1 : 0,
                mr: 4,
              }}
            >
              <Box
                component="img"
                src="/logo.jpeg"
                alt="Starwave Energy Logo"
                sx={{ height: 40, mr: 1.5, borderRadius: '50%' }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                }}
              >
                Starwave Energy
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    color={location.pathname === item.path ? 'primary' : 'inherit'}
                    sx={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      textTransform: 'none',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Right side actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Cart */}
              <IconButton
                component={Link}
                to="/cart"
                color="primary"
                aria-label="shopping cart"
              >
                <Badge badgeContent={getCartItemCount()} color="error">
                  <ShoppingCart />
                </Badge>
              </IconButton>

              {/* User menu */}
              {currentUser ? (
                <>
                  <IconButton
                    onClick={handleUserMenuOpen}
                    color="primary"
                    aria-label="user menu"
                  >
                    <AccountCircle />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleUserMenuClose}>
                      <Typography variant="body2" color="text.secondary">
                        {currentUser.profile.name}
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      component={Link}
                      to="/my-orders"
                      onClick={handleUserMenuClose}
                    >
                      My Orders
                    </MenuItem>
                    {currentUser.role === 'admin' && (
                      <MenuItem
                        component={Link}
                        to="/admin"
                        onClick={handleUserMenuClose}
                      >
                        Admin Dashboard
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                !isMobile && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      component={Link}
                      to="/login"
                      variant="outlined"
                      size="small"
                    >
                      Login
                    </Button>
                    <Button
                      component={Link}
                      to="/register"
                      variant="contained"
                      size="small"
                    >
                      Register
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer />
    </>
  )
}

export default Navbar